import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState, Badge } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import { Lightbulb, Plus, ThumbsUp, Trash2, Flame } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function Suggestions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });

  const load = () => { api.get("/suggestions").then((r) => setItems(r.data)).catch(() => {}); };
  useEffect(load, []);

  const save = async () => {
    if (!form.title) return toast.error("العنوان مطلوب");
    try { await api.post("/suggestions", form); playSuccess(); toast.success("تم إضافة المقترح"); setOpen(false); setForm({ title: "", description: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const vote = async (id) => {
    try { const { data } = await api.post(`/suggestions/${id}/vote`); setItems((prev) => prev.map((s) => s.id === id ? { ...s, voted: data.voted, vote_count: data.vote_count } : s).sort((a, b) => b.vote_count - a.vote_count)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/suggestions/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <InspireBanner />
      <PageTitle title="المقترحات والتصويت" subtitle="فكرتك قد تُشعل شمعةً تُضيء طريق مدرسةٍ بأكملها" icon={Lightbulb}
        actions={<button data-testid="add-suggestion-btn" onClick={() => setOpen(true)} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> مقترح جديد</button>}
      />
      {!items.length ? <EmptyState text="لا توجد مقترحات بعد" icon={Lightbulb} /> : (
        <div className="space-y-3">
          {items.map((s, i) => (
            <div key={s.id} className="glass glass-hover rounded-2xl p-4 flex items-start gap-4">
              <button data-testid={`vote-${s.id}`} onClick={() => vote(s.id)}
                className={`flex flex-col items-center justify-center w-16 py-2 rounded-xl border transition shrink-0 ${s.voted ? "btn-gold" : "bg-amber-50 border-amber-400/30 text-amber-700"}`}>
                <ThumbsUp size={18} /><span className="text-lg font-bold">{s.vote_count}</span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-head font-bold text-stone-800">{s.title}</h4>
                  {i === 0 && s.vote_count > 0 && <Badge className="bg-amber-500/20 text-amber-700 border-amber-400/40 gap-1"><Flame size={12} /> الأكثر طلباً</Badge>}
                </div>
                {s.description && <p className="text-sm text-stone-500 mt-1">{s.description}</p>}
                <p className="text-xs text-stone-400 mt-2">بواسطة {s.author_name}</p>
              </div>
              {(user?.role === "admin" || s.author_id === user?.id) && (
                <button onClick={() => remove(s.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-700 shrink-0"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">مقترح تطويري</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="suggestion-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="عنوان المقترح" className={inputCls} />
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="التفاصيل" className={inputCls} rows={3} />
          </div>
          <DialogFooter><button data-testid="save-suggestion-btn" onClick={save} className="btn-gold px-6 py-2 rounded-xl">إضافة</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
